"use client";

import { useEffect } from "react";

import { X } from "lucide-react";
import { cn } from "@/lib/util/utils";

interface ModalProps {
  isOpen: boolean;
  close: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
  showCloseButton?: boolean;
  disableBackdropClose?: boolean;
}

export const Modal = ({
  isOpen,
  close,
  title,
  children,
  className,
  showCloseButton = true,
  disableBackdropClose = false,
}: ModalProps) => {
  // Manejo de cierre con Escape y bloqueo de scroll
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, close]);

  if (!isOpen) return null;

  return (
    <div
      className="animate-in fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 duration-200"
      onClick={!disableBackdropClose ? close : undefined}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={cn(
          "animate-in zoom-in-95 relative max-h-[90vh] w-fit max-w-70% overflow-y-auto rounded-2xl p-6 shadow-xl duration-200",
          className || "bg-white"
        )}
        onClick={(e) => e.stopPropagation()} // Evita cierre al clickear dentro
      >
        {showCloseButton && (
          <button
            onClick={close}
            className="ring-offset-background focus:ring-ring absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none"
          >
            <X className="h-5 w-5 text-gray-500" />
            <span className="sr-only">Cerrar</span>
          </button>
        )}

        {title && (
          <h2 className="text-orange-main font-achiko mb-6 text-xl font-bold">
            {title}
          </h2>
        )}

        {children}
      </div>
    </div>
  );
};
